import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RefreshCw, AlertTriangle, CheckCircle2, Clock, XCircle, Send } from "lucide-react";

type ChunkStatus = "sent" | "delivered" | "read" | "failed" | "retrying";

interface Chunk {
  id: string;
  org_id: string;
  group_id: string;
  turn_id: string;
  remote_jid: string;
  instance_name: string;
  chunk_index: number;
  chunk_total: number;
  content: string;
  status: ChunkStatus;
  whatsapp_message_id: string | null;
  attempts: number | null;
  last_error: string | null;
  send_acked_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  next_retry_at: string | null;
  last_attempt_at: string | null;
  created_at: string;
}

const statusVariant = (s: ChunkStatus): "default" | "secondary" | "destructive" | "outline" => {
  switch (s) {
    case "read": return "default";
    case "delivered": return "secondary";
    case "sent": return "outline";
    case "retrying": return "outline";
    case "failed": return "destructive";
    default: return "outline";
  }
};

const statusIcon = (s: ChunkStatus) => {
  switch (s) {
    case "read": return <CheckCircle2 className="w-3.5 h-3.5" />;
    case "delivered": return <CheckCircle2 className="w-3.5 h-3.5" />;
    case "sent": return <Send className="w-3.5 h-3.5" />;
    case "retrying": return <Clock className="w-3.5 h-3.5" />;
    case "failed": return <XCircle className="w-3.5 h-3.5" />;
  }
};

export default function OutboundChunks() {
  const [tab, setTab] = useState<"pending" | "failed" | "all">("pending");

  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ["outbound-chunks-stats"],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("outbound_chunks" as any)
        .select("status")
        .gte("created_at", since);
      if (error) throw error;
      const counts: Record<string, number> = { sent: 0, delivered: 0, read: 0, failed: 0, retrying: 0 };
      (data ?? []).forEach((r: any) => {
        counts[r.status] = (counts[r.status] ?? 0) + 1;
      });
      return counts;
    },
    refetchInterval: 30_000,
  });

  const { data: chunks, isLoading, refetch } = useQuery({
    queryKey: ["outbound-chunks", tab],
    queryFn: async () => {
      let q = supabase
        .from("outbound_chunks" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);

      if (tab === "pending") {
        // sent sem ACK assíncrono OU retrying
        q = q.in("status", ["sent", "retrying"]);
      } else if (tab === "failed") {
        q = q.eq("status", "failed");
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as Chunk[];
    },
    refetchInterval: 15_000,
  });

  const refresh = () => {
    refetch();
    refetchStats();
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-headline font-bold flex items-center gap-2">
            <Send className="w-6 h-6 text-primary" />
            Outbound Chunks
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Confirmação de entrega WhatsApp por parte (i/N) — últimas 24h
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Sent (sem ACK)" value={stats?.sent ?? 0} variant="outline" />
        <StatCard label="Delivered" value={stats?.delivered ?? 0} variant="secondary" />
        <StatCard label="Read" value={stats?.read ?? 0} variant="default" />
        <StatCard label="Retrying" value={stats?.retrying ?? 0} variant="outline" />
        <StatCard label="Failed" value={stats?.failed ?? 0} variant="destructive" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Chunks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
            <TabsList>
              <TabsTrigger value="pending">Pendentes</TabsTrigger>
              <TabsTrigger value="failed">Falhos</TabsTrigger>
              <TabsTrigger value="all">Todos</TabsTrigger>
            </TabsList>
            <TabsContent value={tab} className="mt-4">
              {isLoading ? (
                <p className="text-muted-foreground text-sm">Carregando...</p>
              ) : !chunks || chunks.length === 0 ? (
                <p className="text-muted-foreground text-sm py-8 text-center">
                  Nada por aqui — todos os chunks foram entregues.
                </p>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Parte</TableHead>
                        <TableHead>Turn</TableHead>
                        <TableHead>JID</TableHead>
                        <TableHead>Tentativas</TableHead>
                        <TableHead>Conteúdo</TableHead>
                        <TableHead>Erro</TableHead>
                        <TableHead>Criado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {chunks.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <Badge variant={statusVariant(c.status)} className="gap-1">
                              {statusIcon(c.status)}
                              {c.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {c.chunk_index}/{c.chunk_total}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {c.turn_id.slice(0, 8)}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {c.remote_jid.split("@")[0]}
                          </TableCell>
                          <TableCell className="text-sm">{c.attempts ?? 0}</TableCell>
                          <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground" title={c.content}>
                            {c.content}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-xs text-destructive" title={c.last_error ?? ""}>
                            {c.last_error ?? "—"}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(c.created_at).toLocaleString("pt-BR")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "default" | "secondary" | "destructive" | "outline";
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{label}</span>
          <Badge variant={variant} className="text-xs">
            {value}
          </Badge>
        </div>
        <div className="text-2xl font-bold mt-2">{value}</div>
      </CardContent>
    </Card>
  );
}
